import React from "react";
import { Link } from "react-router-dom";
import { usePlayers } from "../apiCalls/playerApiCalls";
import { useClubs, useCourts } from "../apiCalls/clubApiCalls";
import { useTournaments } from "../apiCalls/tournamentApiCalls";
import { Button, Card, PageHeader } from "../components/ui";

// Counts only — ask for a single row and read the total.
const COUNT_ONLY = { limit: 1 };

// A row of the register summary. The label carries the meaning, so the number
// stays quiet and monospaced rather than turned into a headline.
const Figure = ({ label, value, note }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-sand py-3 last:border-0">
    <div>
      <div className="text-sm">{label}</div>
      {note && <div className="text-xs text-ink-soft">{note}</div>}
    </div>
    <div className="font-mono text-xl tabular">
      {value === undefined ? "—" : value}
    </div>
  </div>
);

export default function Overview() {
  const all = usePlayers(COUNT_ONLY);
  const active = usePlayers({ ...COUNT_ONLY, status: "active" });
  const inactive = usePlayers({ ...COUNT_ONLY, status: "inactive" });
  const unclaimed = usePlayers({ ...COUNT_ONLY, claimed: "false" });
  const clubs = useClubs();
  const courts = useCourts();
  const tournaments = useTournaments();

  const waiting = inactive.data?.totalResults;

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle="Where the register stands today."
      />

      {/* The one thing an administrator actually has to act on: profiles that
          registered themselves and are waiting to be let into the rankings. */}
      {waiting > 0 && (
        <Card className="mb-8 flex flex-wrap items-center justify-between gap-4 border-l-2 border-l-ball p-5">
          <div>
            <div className="font-display text-lg font-bold uppercase tracking-tight">
              {waiting} profile{waiting === 1 ? "" : "s"} waiting
            </div>
            <p className="mt-1 text-sm text-ink-soft">
              Inactive players don't appear in the public rankings. Review them
              and set the ones you recognise to active.
            </p>
          </div>
          <Link to="/players">
            <Button variant="primary">Review players</Button>
          </Link>
        </Card>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-ink-soft">
            The register
          </h2>
          <div className="mt-3">
            <Figure label="Players" value={all.data?.totalResults} />
            <Figure
              label="Active"
              note="Visible in the public rankings"
              value={active.data?.totalResults}
            />
            <Figure
              label="Inactive"
              note="Entered but not yet verified"
              value={waiting}
            />
            <Figure
              label="Without an account"
              note="Entered by an organiser, never signed up"
              value={unclaimed.data?.totalResults}
            />
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-ink-soft">
            Venues and calendar
          </h2>
          <div className="mt-3">
            <Figure label="Clubs" value={clubs.data?.length} />
            <Figure
              label="Courts"
              note="What the scheduler has to work with"
              value={courts.data?.length}
            />
            <Figure label="Tournaments" value={tournaments.data?.length} />
          </div>
        </Card>
      </div>
    </>
  );
}
